import { z } from 'zod';

import { laboratoryApi, toSearchParams } from '@/lib/http';
import { tolerantArray } from '@/lib/tolerant-array';

import { fetchAllPages, MAX_PER_PAGE } from './paginated-fetch';

const MESSAGES_ENDPOINT = 'api/v1/messages';

export const MESSAGE_MODELS = {
  determinations: 'determinacao',
  equipments: 'equipamento_laboratorio',
  installationSample: 'instalacao_amostra',
  inertMaterials: 'material_inerte_laboratorio',
  species: 'especie',
  specialTreatments: 'tratamento_especial_laboratorio',
  substrates: 'substrato_laboratorio',
  systemPreferences: 'preferencia_sistema',
  toleranceTables: 'tabela_tolerancia',
} as const;

export type MessageModel = (typeof MESSAGE_MODELS)[keyof typeof MESSAGE_MODELS];

export const messageMetadataSchema = z.object({
  id: z.string(),
  deleted: z.boolean().nullish(),
});

type MessageMetadata = z.output<typeof messageMetadataSchema>;

function messagesResponseSchema<TSchema extends z.ZodType<MessageMetadata, z.ZodTypeDef, unknown>>(
  itemSchema: TSchema,
  model: MessageModel,
) {
  return z.object({
    messages: z.object({
      total_pages: z
        .number()
        .nullish()
        .transform((totalPages) => totalPages ?? 1),
      data: tolerantArray(itemSchema, `${MESSAGES_ENDPOINT}?model=${model}`),
    }),
  });
}

export async function fetchMessages<
  TSchema extends z.ZodType<MessageMetadata, z.ZodTypeDef, unknown>,
>(
  model: MessageModel,
  itemSchema: TSchema,
  filters: Record<string, string> = {},
): Promise<z.output<TSchema>[]> {
  const responseSchema = messagesResponseSchema(itemSchema, model);

  const items = await fetchAllPages(async (page) => {
    const searchParams = toSearchParams({ ...filters, model, per_page: MAX_PER_PAGE, page });
    const payload = await laboratoryApi.get(MESSAGES_ENDPOINT, { searchParams }).json();

    return responseSchema.parse(payload).messages;
  });

  return items.filter((item) => item.deleted !== true);
}

export async function updateMessage(id: string, body: Record<string, unknown>): Promise<void> {
  await laboratoryApi.put(`${MESSAGES_ENDPOINT}/${id}`, { json: body }).json();
}
